// Global Instructions Rule Applied! - March 31, 2026
import { useCallback, useEffect, useMemo } from 'react'
import { Input, InputNumber, Select, Space } from 'antd'
import DatePicker from '../DatePicker.tsx'
import dayjs from 'dayjs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import Button from '../Button.jsx'
import type { FilterOptions } from '../TableView/TableViewTypes.ts'
import type { ViewConfigColumn } from './types.ts'
import { ContactInput } from '../InputManager/components/ContactInput.tsx'
import {
  isContactFilterColumn,
  isCustomFilterColumn,
  isDateRangeFilterColumn,
  isRangeFilterColumn,
  isSelectFilterColumn,
  isTextFilterColumn
} from './filterColumnKind.ts'

// Formatter / parser pair for numeric range inputs. Mirrors the cfg-driven
// formatting used by the column-header RangeFilterDropdown so the drawer
// shows the same thousand-separator (and "X%" suffix for percent columns)
// the user sees in cell values.
const FORMATTED_NUMERIC_FIELD_TYPES = new Set<string>(['currency', 'number', 'valuation', 'percent', 'ratio'])
const numberFormatter = (value: string | number | undefined) =>
  `${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
const numberParser = (value: string | undefined) => (value ?? '').replace(/,/g, '')
const percentFormatter = (value: string | number | undefined) => {
  if (value === undefined || value === null || value === '') return ''
  return `${numberFormatter(value)}%`
}
const percentParser = (value: string | undefined) => (value ?? '').replace(/%/g, '').replace(/,/g, '')

/** Same as TableView column `filterKey || valueKey` — used to read `filterOptions[key]` */
function filterOptionsLookupKey(col: ViewConfigColumn | undefined): string | undefined {
  if (!col) return undefined
  return col.filterKey || col.valueKey
}

/** Same mapping as `MultiselectFilterDropdown` */
function mapFilterOptionsToSelect(filterOptions: FilterOptions | undefined, key: string | undefined) {
  if (!key || !filterOptions?.[key]) return []
  return filterOptions[key].map((option) => ({
    label: typeof option === 'string' ? option : option.label || (option as { name?: string }).name || String(option),
    value: typeof option === 'string' ? option : ((option as { value?: string | number }).value ?? option)
  }))
}

type FilterOperator = string

export interface FilterDraftRow {
  id: string
  valueKey: string | undefined
  operator: FilterOperator
  /** Text / single; range as "min|max"; multiselect as JSON string array */
  value: string
}

function defaultOperatorForColumn(col: ViewConfigColumn | undefined): FilterOperator {
  if (!col) return 'contains'
  if (isRangeFilterColumn(col)) return 'between'
  if (isDateRangeFilterColumn(col)) return 'between'
  if (isContactFilterColumn(col)) return 'any_of'
  if (isSelectFilterColumn(col)) return 'any_of'
  if (isCustomFilterColumn(col)) return 'custom'
  return 'contains'
}

function parseMultiValue(raw: string): string[] {
  if (!raw?.trim()) return []
  try {
    const p = JSON.parse(raw) as unknown
    return Array.isArray(p) ? p.map(String) : []
  } catch {
    return []
  }
}

/**
 * A multiselect row's value read back for its Select, each entry typed like the option it names.
 * `parseMultiValue` returns strings, but an integer-keyed lookup (e.g. `cfg_asset_transaction_types__id`)
 * carries numeric option values, and antd Select matches values strictly - "3" against option 3 renders
 * the raw id as the tag instead of the option's label.
 */
function selectValueForOptions(raw: string, options: ReturnType<typeof mapFilterOptionsToSelect>) {
  const optionValueByKey = new Map(options.map((o) => [String(o.value), o.value]))
  return parseMultiValue(raw).map((v) => optionValueByKey.get(v) ?? v)
}

function shouldHideFromFilterPicker(col: ViewConfigColumn): boolean {
  return col.valueKey === 'prog__tasks' || col.columnTitle === 'Tasks'
}

let rowId = 0
export function createFilterDraftRow(seed?: Partial<FilterDraftRow>): FilterDraftRow {
  rowId += 1
  return {
    id: `filter-row-${rowId}-${Date.now()}`,
    valueKey: seed?.valueKey,
    operator: seed?.operator ?? 'contains',
    value: seed?.value ?? ''
  }
}

export interface FilterPanelProps {
  columns: ViewConfigColumn[]
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  value: FilterDraftRow[]
  onChange: (next: FilterDraftRow[]) => void
}

export function FilterPanel({ columns, filterOptions, filterOptionsLoading, value, onChange }: FilterPanelProps) {
  const filterableColumns = useMemo(
    () => columns.filter((c) => c.filterable !== false && !shouldHideFromFilterPicker(c)),
    [columns]
  )
  const rows = useMemo<FilterDraftRow[]>(
    () => (value.length > 0 ? value : [createFilterDraftRow()]),
    [value]
  )

  // Dev-only: surface producer bugs. An active filter row's `valueKey` must
  // be a filterable column's `field_name`. Anything else is a producer that
  // emitted a non-canonical identifier (filter_key, sort_key, a stale pref
  // from older code, etc.) — the Select can't find it in the column options,
  // so the row renders against an unresolved column. Stripped from prod
  // builds by dead-code elimination on `process.env.NODE_ENV`.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    if (filterableColumns.length === 0) return
    const knownKeys = new Set(filterableColumns.map((c) => c.valueKey))
    for (const row of rows) {
      if (row.valueKey && !knownKeys.has(row.valueKey)) {
        console.warn(
          `[FilterPanel] active filter row references "${row.valueKey}", which is not a filterable column's field_name. ` +
            `The row will render against an unresolved column. ` +
            `Check the producer that emitted this key (default-filter constant, column-header handler, or a stored user preference written by older code).`
        )
      }
    }
  }, [rows, filterableColumns])

  const columnOptions = useMemo(
    () =>
      filterableColumns
        .map((c) => ({
          value: c.valueKey,
          label: c.columnTitle
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [filterableColumns]
  )

  const updateRow = useCallback(
    (id: string, patch: Partial<FilterDraftRow>) => {
      const nextRows = rows.map((r) => {
        if (r.id !== id) return r
        const next = { ...r, ...patch }
        if (patch.valueKey !== undefined) {
          const col = filterableColumns.find((c) => c.valueKey === patch.valueKey)
          next.operator = defaultOperatorForColumn(col)
          if (patch.valueKey !== r.valueKey) next.value = ''
        }
        return next
      })
      onChange(nextRows)
    },
    [rows, filterableColumns, onChange]
  )

  const addRow = useCallback(() => {
    onChange([...rows, createFilterDraftRow()])
  }, [rows, onChange])

  const removeRow = useCallback((id: string) => {
    const nextRows = rows.filter((r) => r.id !== id)
    onChange(nextRows.length > 0 ? nextRows : [createFilterDraftRow()])
  }, [rows, onChange])

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col gap-3'>
      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1'>
        {rows.map((row) => {
          const col = filterableColumns.find((c) => c.valueKey === row.valueKey)
          const isRange = col && isRangeFilterColumn(col)
          const isDateRange = col && isDateRangeFilterColumn(col)
          const isContact = col && isContactFilterColumn(col)
          const isSelect = col && isSelectFilterColumn(col)
          const isText = col && isTextFilterColumn(col)
          const isCustom = col && isCustomFilterColumn(col)
          const selectOptions = mapFilterOptionsToSelect(filterOptions, filterOptionsLookupKey(col))
          const contactFieldValue = parseMultiValue(row.value).map((id) => ({
            value: id,
            label: id
          }))

          const [minStr, maxStr] = (row.value ?? '').split('|')
          const minVal = minStr === '' || minStr === undefined ? null : Number(minStr)
          const maxVal = maxStr === '' || maxStr === undefined ? null : Number(maxStr)
          // Percent columns store the underlying decimal (0.125 = "12.5%"); the
          // input shows the user-facing percentage and translates on the way in.
          const isPercent = (col?.fieldType ?? '') === 'percent'
          const isFormattedNumeric =
            !!col && FORMATTED_NUMERIC_FIELD_TYPES.has(col.fieldType ?? '')
          const toDisplay = (v: number | null): number | null =>
            v === null || Number.isNaN(v) ? null : isPercent ? Number((v * 100).toFixed(10)) : v
          const fromDisplay = (v: number | null | undefined): number | null =>
            v === null || v === undefined || Number.isNaN(v) ? null : isPercent ? Number((v / 100).toFixed(10)) : v

          return (
            <div key={row.id} className='rounded-md border border-gray-200 bg-gray-50/80 p-3'>
              <Space direction='vertical' className='w-full' size={6}>
                <div className='flex min-w-0 items-center gap-2'>
                  <Select
                    placeholder='Select column'
                    allowClear
                    className='w-full flex-1'
                    options={columnOptions}
                    value={row.valueKey}
                    onChange={(v) => updateRow(row.id, { valueKey: v })}
                  />
                  {rows.length > 1 && (
                    <button
                      type='button'
                      aria-label='Remove filter'
                      className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 text-red-600 hover:bg-red-50'
                      onClick={() => removeRow(row.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} className='text-sm' />
                    </button>
                  )}
                </div>

                {isRange && row.valueKey && (
                  <>
                    <Space.Compact className='w-full'>
                      <InputNumber
                        className='!w-1/2'
                        placeholder='Min'
                        value={toDisplay(minVal)}
                        formatter={
                          isFormattedNumeric ? (isPercent ? percentFormatter : numberFormatter) : undefined
                        }
                        parser={
                          isFormattedNumeric ? (isPercent ? percentParser : numberParser) : undefined
                        }
                        onChange={(v) => {
                          const stored = fromDisplay(v as number | null)
                          const max = maxStr ?? ''
                          updateRow(row.id, {
                            value: `${stored ?? ''}|${max}`,
                            operator: 'between'
                          })
                        }}
                      />
                      <InputNumber
                        className='!w-1/2'
                        placeholder='Max'
                        value={toDisplay(maxVal)}
                        formatter={
                          isFormattedNumeric ? (isPercent ? percentFormatter : numberFormatter) : undefined
                        }
                        parser={
                          isFormattedNumeric ? (isPercent ? percentParser : numberParser) : undefined
                        }
                        onChange={(v) => {
                          const stored = fromDisplay(v as number | null)
                          const min = minStr ?? ''
                          updateRow(row.id, {
                            value: `${min}|${stored ?? ''}`,
                            operator: 'between'
                          })
                        }}
                      />
                    </Space.Compact>
                  </>
                )}

                {isDateRange && row.valueKey && (
                  <Space.Compact className='w-full'>
                    <DatePicker
                      className='!w-1/2'
                      placeholder='From'
                      format='YYYY-MM-DD'
                      value={minStr ? dayjs(minStr) : null}
                      onChange={(d) => {
                        const max = maxStr ?? ''
                        const next = d ? d.format('YYYY-MM-DD') : ''
                        updateRow(row.id, {
                          value: `${next}|${max}`,
                          operator: 'between'
                        })
                      }}
                    />
                    <DatePicker
                      className='!w-1/2'
                      placeholder='To'
                      format='YYYY-MM-DD'
                      value={maxStr ? dayjs(maxStr) : null}
                      onChange={(d) => {
                        const min = minStr ?? ''
                        const next = d ? d.format('YYYY-MM-DD') : ''
                        updateRow(row.id, {
                          value: `${min}|${next}`,
                          operator: 'between'
                        })
                      }}
                    />
                  </Space.Compact>
                )}

                {isContact && row.valueKey && !isCustom && (
                  <ContactInput
                    mode='multiple'
                    size='small'
                    placeholder='Search contacts'
                    value={contactFieldValue}
                    onChange={(next) => {
                      const arr = Array.isArray(next) ? next : []
                      const ids = arr
                        .map((x: { value?: string }) => (typeof x === 'object' && x?.value ? String(x.value) : ''))
                        .filter(Boolean)
                      updateRow(row.id, { value: JSON.stringify(ids), operator: 'any_of' })
                    }}
                    allowAddNew={false}
                  />
                )}

                {isSelect && row.valueKey && !isCustom && !isContact && (
                  <>
                    <Select
                      mode='multiple'
                      allowClear
                      showSearch
                      className='w-full'
                      placeholder='Select one or more values'
                      options={selectOptions}
                      loading={filterOptionsLoading}
                      value={selectValueForOptions(row.value, selectOptions)}
                      onChange={(vals) => updateRow(row.id, { value: JSON.stringify(vals ?? []) })}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </>
                )}

                {isText && row.valueKey && !isCustom && (
                  <>
                    <Input
                      placeholder='Enter text'
                      value={row.value}
                      onChange={(e) => updateRow(row.id, { value: e.target.value })}
                    />
                  </>
                )}
              </Space>
            </div>
          )
        })}
        <Button
          variant='default'
          className='w-full shrink-0 border border-dashed border-gray-300'
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={addRow}
        >
          Add filter
        </Button>
      </div>
    </div>
  )
}
